import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { propFirmData } from '../data/propFirmDataHomepage';
import BestPropFirmsHero from '../components/BestPropFirmsHero';
import FilterBarPro from '../components/FilterBarPro';
import ComparisonTablePro from '../components/ComparisonTablePro';
import FirmPromoCarouselPro from '../components/FirmPromoCarouselPro';

export function BestPropFirmsPage() {
  const [sortKey, setSortKey] = useState<'maxFunding' | 'profitSplit' | 'rating'>('rating');
  const [filters, setFilters] = useState({
    search: '',
    evaluation: [],
    platforms: [],
    payout: [],
    drawdown: [],
    profitSplit: [],
    maxFunding: []
  });

  const firms = useMemo(() => {
    return Object.values(propFirmData)
      .filter(firm => {
        const matchesSearch = firm.name.toLowerCase().includes(filters.search.toLowerCase());

        const matchesFilters = ['evaluation', 'platforms', 'payout', 'drawdown', 'profitSplit', 'maxFunding'].every(key => {
          if (filters[key].length === 0) return true;

          const firmValues = (() => {
            switch (key) {
              case 'evaluation': return firm.evaluation?.types || [];
              case 'platforms': return firm.features?.platforms || [];
              case 'payout': return firm.features?.payout ? [firm.features.payout] : [];
              case 'drawdown': return firm.features?.maxDrawdown ? [firm.features.maxDrawdown.split(',')[0].trim()] : [];
              case 'profitSplit': return firm.features?.profitSplit ? [firm.features.profitSplit] : [];
              case 'maxFunding': return firm.features?.maxFunding ? [firm.features.maxFunding] : [];
              default: return [];
            }
          })();

          return filters[key].every(f => firmValues.includes(f));
        });

        return matchesSearch && matchesFilters;
      })
      .filter(firm => firm.features && firm.evaluation)
      .sort((a, b) => {
        if (sortKey === 'rating') return b.rating - a.rating;
        if (sortKey === 'maxFunding') {
          const aVal = parseInt(a.features.maxFunding.replace(/[$,]/g, ''));
          const bVal = parseInt(b.features.maxFunding.replace(/[$,]/g, ''));
          return bVal - aVal;
        }
        if (sortKey === 'profitSplit') {
          const aVal = parseInt(a.features.profitSplit.replace(/[^0-9]/g, ''));
          const bVal = parseInt(b.features.profitSplit.replace(/[^0-9]/g, ''));
          return bVal - aVal;
        }
        return 0;
      });
  }, [sortKey, filters]);

  return (
    <>
      <Helmet>
        <title>Best Prop Trading Firms 2025 | PropViper</title>
        <meta
          name="description"
          content="Discover the best prop trading firms in 2025. Compare discounts, features, and get exclusive promo codes to save on your next challenge."
        />
        <link rel="canonical" href="https://propviper.com/best-prop-firms" />
      </Helmet>

      <BestPropFirmsHero />

      <section className="bg-gray-950 py-12">
        <div className="container mx-auto px-4">
          <FilterBarPro filters={filters} onChange={setFilters} firms={Object.values(propFirmData)} />

          <div className="overflow-x-auto rounded-2xl border border-yellow-700">
            <ComparisonTablePro firms={firms} />
          </div>
        </div>
      </section>

      <section className="bg-gray-950 py-16">
        <div className="container mx-auto px-4">
          <FirmPromoCarouselPro firms={firms} />
        </div>
      </section>

      <section className="bg-gray-900 py-10 text-center">
        <h2 className="text-2xl font-bold text-yellow-400 mb-2">Ready to Get Funded?</h2>
        <p className="text-yellow-300 mb-4">Compare the best prop firms & claim your exclusive promo code today.</p>
        <a href="#compare-table" className="bg-yellow-400 text-black px-6 py-3 rounded-full font-bold inline-block hover:bg-yellow-300 transition">
          Compare Firms Now
        </a>
      </section>
    </>
  );
}

export default BestPropFirmsPage;